import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, Loader2 } from "lucide-react";

export function MaintenanceToggle() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/maintenance").then(({ data }) => { setEnabled(!!data.enabled); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  const toggle = async () => {
    const next = !enabled;
    if (next && !window.confirm("Aktive Maintenance Mode? Tout kliyan yo ap bloke jiskaske ou dezaktive l. (Admin/staff toujou ka konekte.)")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/config/maintenance", { enabled: next });
      setEnabled(data.enabled);
      toast.success(data.enabled ? "Maintenance Mode AKTIVE — kliyan yo bloke" : "Maintenance Mode DEZAKTIVE — app la louvri");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="maintenance-toggle" className={`mb-5 rounded-lg border p-3.5 flex items-center justify-between transition-colors ${enabled ? "border-[#ff2d2d]/50 bg-[#ff2d2d]/8" : "border-[#222] bg-[#0F0F0F]"}`}>
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${enabled ? "bg-[#ff2d2d]/20 text-[#ff5b5b]" : "bg-[#1A1A1A] text-[#888]"}`}>
          <Wrench className="h-4.5 w-4.5" />
        </div>
        <div>
          <div className="text-sm font-semibold flex items-center gap-2">
            Maintenance Mode
            {enabled && <span className="font-mono-term text-[9px] uppercase px-1.5 py-0.5 rounded bg-[#ff2d2d]/20 text-[#ff5b5b]">Aktive</span>}
          </div>
          <div className="text-xs text-[#888]">{enabled ? "Kliyan yo bloke. Sèlman admin/staf ka konekte." : "App la louvri pou tout moun."}</div>
        </div>
      </div>
      <button
        data-testid="maintenance-switch"
        onClick={toggle}
        disabled={busy || !loaded}
        role="switch"
        aria-checked={enabled}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${enabled ? "bg-[#ff2d2d]" : "bg-[#333]"} disabled:opacity-50`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin text-white mx-auto" /> : (
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
        )}
      </button>
    </div>
  );
}

export function MaintenancePage() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="maintenance-page">
      <div className="w-full max-w-2xl rounded-2xl border-t-2 border-[#ff2d2d] bg-[#0d0d0f] p-8 sm:p-12 text-center">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/smartfx-logo.png" alt="Smart FX" className="h-9" />
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#ff2d2d]/50 mb-6">
          <span className="h-2 w-2 rounded-full bg-[#ff2d2d] animate-pulse" />
          <span className="font-mono-term text-[11px] font-bold uppercase tracking-widest text-[#ff5b5b]">Scheduled Maintenance</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">We'll Be Right Back</h1>
        <p className="text-[#b9b9c0] text-base leading-relaxed max-w-xl mx-auto">
          Smart FX is currently undergoing scheduled maintenance to enhance the performance, security, and
          reliability of your AutoTrade dashboard. During this time, sign-in and platform access are
          temporarily unavailable. Our team is working to complete the upgrade as quickly as possible, and
          all systems will be back online shortly. Your account, balances, and funds remain fully safe and
          secure. We sincerely appreciate your patience and apologize for any inconvenience.
        </p>
        <div className="mt-8 pt-6 border-t border-[#222]">
          <p className="text-[#cfcfd6] text-sm">Thank you for choosing Smart FX — AutoTrade Platform</p>
          <p className="text-[#6b6b73] text-xs mt-1">For urgent assistance, please contact our support team.</p>
        </div>
      </div>
    </div>
  );
}

export function MaintenanceGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const { data } = await api.get("/maintenance");
        if (alive) setEnabled(!!data.enabled);
      } catch { /* ignore */ }
    };
    check();
    const id = setInterval(check, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const isStaff = user && ["admin", "manager"].includes(user.role);
  const path = location.pathname;
  // Keep only the login page reachable (so staff can authenticate). Everything else
  // is blocked for non-staff while maintenance is on.
  const loginPath = path === "/login";

  if (enabled && !isStaff && !loginPath) return <MaintenancePage />;
  return children;
}
